import { type RefObject, useCallback, useRef, useState } from "react"
import { useDebounceValue } from "usehooks-ts"

export function useMentionDetection(
	textareaRef: RefObject<HTMLTextAreaElement | null>,
	setContent: (value: string) => void,
) {
	// ── Mention state ──
	const [mentionQuery, setMentionQuery] = useState<string | null>(null)
	const [debouncedMentionQuery] = useDebounceValue(mentionQuery ?? "", 250)
	const mentionStartRef = useRef(0)
	const [selectedIndex, setSelectedIndex] = useState(0)

	// ── Ticker state ──
	const [tickerQuery, setTickerQuery] = useState<string | null>(null)
	const [debouncedTickerQuery] = useDebounceValue(tickerQuery ?? "", 250)
	const tickerStartRef = useRef(0)
	const [selectedTickerIndex, setSelectedTickerIndex] = useState(0)

	// Reset selected index when debounced query changes
	const prevMentionRef = useRef(debouncedMentionQuery)
	if (prevMentionRef.current !== debouncedMentionQuery) {
		prevMentionRef.current = debouncedMentionQuery
		setSelectedIndex(0)
	}
	const prevTickerRef = useRef(debouncedTickerQuery)
	if (prevTickerRef.current !== debouncedTickerQuery) {
		prevTickerRef.current = debouncedTickerQuery
		setSelectedTickerIndex(0)
	}

	const detectMention = useCallback(() => {
		const textarea = textareaRef.current
		if (!textarea) {
			return
		}
		const text = textarea.value
		const cursorPos = textarea.selectionStart

		// Try @ mention first
		let i = cursorPos - 1
		while (i >= 0 && /[a-z0-9_]/i.test(text[i])) {
			i--
		}
		if (i >= 0 && text[i] === "@" && (i === 0 || /\s/.test(text[i - 1]))) {
			const partial = text.slice(i + 1, cursorPos).toLowerCase()
			mentionStartRef.current = i
			setMentionQuery(partial.length >= 3 ? partial : null)
			setTickerQuery(null)
			return
		}
		setMentionQuery(null)

		// Try $ ticker
		let j = cursorPos - 1
		while (j >= 0 && /[a-zA-Z]/.test(text[j])) {
			j--
		}
		if (j >= 0 && text[j] === "$" && (j === 0 || /\s/.test(text[j - 1]))) {
			const partial = text.slice(j + 1, cursorPos)
			tickerStartRef.current = j
			setTickerQuery(partial.length >= 2 ? partial : null)
			return
		}
		setTickerQuery(null)
	}, [textareaRef])

	const insertMention = useCallback(
		(handle: string) => {
			const textarea = textareaRef.current
			if (!textarea) {
				return
			}
			const start = mentionStartRef.current
			const cursorPos = textarea.selectionStart
			const text = textarea.value
			const before = text.slice(0, start)
			const after = text.slice(cursorPos)
			setContent(`${before}@${handle} ${after}`)
			setMentionQuery(null)
			setSelectedIndex(0)

			const newPos = start + handle.length + 2 // @ + handle + space
			requestAnimationFrame(() => {
				textarea.focus()
				textarea.setSelectionRange(newPos, newPos)
			})
		},
		[textareaRef, setContent],
	)

	const insertTicker = useCallback(
		(ticker: string) => {
			const textarea = textareaRef.current
			if (!textarea) {
				return
			}
			const start = tickerStartRef.current
			const cursorPos = textarea.selectionStart
			const text = textarea.value
			const before = text.slice(0, start)
			const after = text.slice(cursorPos)
			setContent(`${before}$${ticker} ${after}`)
			setTickerQuery(null)
			setSelectedTickerIndex(0)

			const newPos = start + ticker.length + 2 // $ + ticker + space
			requestAnimationFrame(() => {
				textarea.focus()
				textarea.setSelectionRange(newPos, newPos)
			})
		},
		[textareaRef, setContent],
	)

	const dismissMentions = useCallback(() => {
		setMentionQuery(null)
		setSelectedIndex(0)
	}, [])

	const dismissTickers = useCallback(() => {
		setTickerQuery(null)
		setSelectedTickerIndex(0)
	}, [])

	const showMentionSuggestions = mentionQuery !== null && debouncedMentionQuery.length >= 3
	const showTickerSuggestions = tickerQuery !== null && debouncedTickerQuery.length >= 2

	const handleKeyDown = useCallback(
		(
			e: React.KeyboardEvent<HTMLTextAreaElement>,
			mentionItems: { handle: string }[],
			tickerItems: { ticker: string }[],
		) => {
			// Mention suggestions active
			if (showMentionSuggestions && mentionItems.length > 0) {
				if (e.key === "ArrowDown") {
					e.preventDefault()
					setSelectedIndex(i => Math.min(i + 1, mentionItems.length - 1))
				} else if (e.key === "ArrowUp") {
					e.preventDefault()
					setSelectedIndex(i => Math.max(i - 1, 0))
				} else if (e.key === "Enter" || e.key === "Tab") {
					e.preventDefault()
					const item = mentionItems[selectedIndex]
					if (item) {
						insertMention(item.handle)
					}
				} else if (e.key === "Escape") {
					e.preventDefault()
					dismissMentions()
				}
				return
			}

			// Ticker suggestions active
			if (showTickerSuggestions && tickerItems.length > 0) {
				if (e.key === "ArrowDown") {
					e.preventDefault()
					setSelectedTickerIndex(i => Math.min(i + 1, tickerItems.length - 1))
				} else if (e.key === "ArrowUp") {
					e.preventDefault()
					setSelectedTickerIndex(i => Math.max(i - 1, 0))
				} else if (e.key === "Enter" || e.key === "Tab") {
					e.preventDefault()
					const item = tickerItems[selectedTickerIndex]
					if (item) {
						insertTicker(item.ticker)
					}
				} else if (e.key === "Escape") {
					e.preventDefault()
					dismissTickers()
				}
			}
		},
		[
			showMentionSuggestions,
			showTickerSuggestions,
			selectedIndex,
			selectedTickerIndex,
			insertMention,
			insertTicker,
			dismissMentions,
			dismissTickers,
		],
	)

	return {
		detectMention,
		dismissMentions,
		dismissTickers,
		handleKeyDown,
		insertMention,
		insertTicker,
		mentionQuery: debouncedMentionQuery,
		selectedIndex,
		selectedTickerIndex,
		showMentionSuggestions,
		showTickerSuggestions,
		tickerQuery: debouncedTickerQuery,
	}
}
